import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useRegisterUnsavedChanges } from "../../../contexts/UnsavedChangesContext";
import { useToast } from "../../../contexts/ToastContext";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectTherapistProfile,
  selectClinicProfile,
  selectIsAdmin,
  selectIsSecretary,
  selectHasPermission,
  selectCanAccessFinance,
  selectClinicManagesCouplesAsPatients,
  selectEnglishCommunicationEnabled,
} from "../../../store/slices/authSlice";
import { isAssignableTherapist } from "../../../config/permissionsRegistry";
import { emptyPartner } from "../../../utils/coupleUtils";
import { useGetClinicTherapistsQuery } from "../../../store/services/therapistApi";
import {
  clearFormDraft,
  selectFormDraft,
} from "../../../store/slices/patientsSlice";
import { useCreatePatientMutation } from "../../../store/services/patientsApi";
import { useGetCategoriesQuery } from "../../../store/services/tasksApi";
import usePlanPermissions from "../../../hooks/usePlanPermissions";
import { persistCustomReferralSource } from "../../../utils/referralSources";
import { formatLocalDateString } from "../../../utils/dateUtils";
import {
  uploadPatientFile,
  createPatientFile,
  createPatientFolder,
} from "../../../firebase";

export const PATIENT_TABS = {
  BASIC: "basic",
  DETAILS: "details",
  MEDICAL: "medical",
  ADMIN: "admin",
  FILES: "files",
};

export const HEALTH_FUND_OPTIONS = [
  { value: "clalit", label: "כללית" },
  { value: "maccabi", label: "מכבי" },
  { value: "meuhedet", label: "מאוחדת" },
  { value: "leumit", label: "לאומית" },
  { value: "private", label: "פרטי" },
];

export const STATUS_OPTIONS = [
  { value: "active", label: "פעיל" },
  { value: "waiting", label: "בהמתנה" },
  { value: "finished", label: "סיים טיפול" },
  { value: "initial", label: "פנייה ראשונית" },
];

export const LANGUAGE_OPTIONS = [
  "עברית",
  "ערבית",
  "רוסית",
  "אנגלית",
  "אמהרית",
  "צרפתית",
  "ספרדית",
];

const initialFormData = {
  // Basic (Required)
  firstName: "",
  lastName: "",
  phone: "",
  referralReason: "",

  // Couple (when the clinic manages couples as patients)
  isCouple: false,
  partner1: { firstName: "", lastName: "", phone: "", idNumber: "" },
  partner2: { firstName: "", lastName: "", phone: "", idNumber: "" },
  smsPrimaryPartner: 1,

  // Personal
  idNumber: "",
  birthDate: "",
  gender: "",
  languages: [],
  // SMS + confirmation page language. Only ever "en" in a clinic that opted in
  // to English-speaking patients; every existing patient reads as Hebrew.
  communicationLanguage: "he",
  email: "",
  address: { city: "", street: "", number: "" },

  // Parents
  parent1: { name: "", phone: "", id: "" },
  parent2: { name: "", phone: "", id: "" },
  familyNotes: "",

  // Clinical
  diagnoses: "",
  healthFund: "",
  referringDoctor: "",
  allergies: "",
  medications: "",
  previousTreatments: "",
  educationalFramework: "",
  hearingTestResults: "",

  // Admin
  status: "active",
  patientTypeIds: [], // Array of patient type IDs
  joinDate: formatLocalDateString(new Date()), // Use Israel timezone
  referralSource: "",
  treatmentRate: "",
  treatmentDuration: "",
  treatmentFrequency: "",
  defaultTreatmentTypeId: "",
  preferredSchedule: {},

  // Emergency
  emergencyContact: { name: "", relation: "", phone: "" },

  // Notes
  adminNotes: "",
  clinicalNotes: "",
};

const PARTNER_ERROR_SUFFIX = {
  firstName: "FirstName",
  lastName: "LastName",
  phone: "Phone",
  idNumber: "Id",
};

/**
 * All state + business logic for the new-patient wizard. Moved verbatim from
 * the old tabbed CreatePatientPage, plus wizard step machinery modeled on
 * calendar/appointmentWizard/useAppointmentForm.
 */
function usePatientForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicProfile = useSelector(selectClinicProfile);
  const isAdmin = useSelector(selectIsAdmin);
  const canAccessFinance = useSelector(selectCanAccessFinance);
  const clinicManagesCouples = useSelector(selectClinicManagesCouplesAsPatients);
  const englishCommunicationEnabled = useSelector(
    selectEnglishCommunicationEnabled
  );
  const savedDraft = useSelector(selectFormDraft);

  // Admin / "patients.createForOthers" holders in a multi-therapist clinic can
  // assign the patient to a colleague. Secretaries have no patients of their
  // own, so for them picking a therapist is mandatory and self is excluded.
  const isSecretary = useSelector(selectIsSecretary);
  const canCreateForOthers = useSelector(selectHasPermission("patients.createForOthers"));
  const canAssignToOtherTherapist =
    (isAdmin || canCreateForOthers) && !clinicProfile?.isSoloClinic;
  const therapistRequired = isSecretary;
  // A secretary cannot own patients, so without the right to assign one to a
  // colleague (patients.createForOthers off, or a solo clinic) there is
  // nobody to create the patient for — the wizard shows a no-access state
  // instead of demanding a therapist in a field it never renders.
  const creationBlocked = isSecretary && !canAssignToOtherTherapist;
  const [selectedTherapistId, setSelectedTherapistId] = useState(
    isSecretary ? "" : therapistId
  );

  const { data: clinicTherapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId || !canAssignToOtherTherapist }
  );

  const activeTherapists = useMemo(
    () =>
      clinicTherapists.filter(
        (t) => isAssignableTherapist(t) && (!isSecretary || t.uid !== therapistId)
      ),
    [clinicTherapists, isSecretary, therapistId]
  );

  // Update selectedTherapistId when therapistId loads (secretaries must pick)
  useEffect(() => {
    if (therapistId && !selectedTherapistId && !isSecretary) {
      setSelectedTherapistId(therapistId);
    }
  }, [therapistId, selectedTherapistId, isSecretary]);

  const effectiveTherapistId = canAssignToOtherTherapist ? selectedTherapistId : therapistId;
  const effectiveTherapistName = useMemo(() => {
    if (!canAssignToOtherTherapist) return therapistName;
    const selected = activeTherapists.find((t) => t.uid === selectedTherapistId);
    return selected ? `${selected.firstName} ${selected.lastName}` : therapistName;
  }, [canAssignToOtherTherapist, selectedTherapistId, activeTherapists, therapistName]);

  // Determine if this is a speech therapist clinic
  const isSpeechTherapist = clinicProfile?.type === "speech_therapist";

  // Toast notifications
  const { showSuccess, showError } = useToast();

  // Plan permissions hook
  const { checkPatient, isChecking, PlanLimitModalComponent } = usePlanPermissions();

  // Get patient types from therapist profile
  const patientTypes = therapistProfile?.patientTypes || [];

  // Get treatment types for default treatment type selector
  const { data: treatmentTypes = [] } = useGetCategoriesQuery(
    { clinicId, type: "treatmentType" },
    { skip: !clinicId }
  );

  // ===== Tabs =====
  const [activeTab, setActiveTab] = useState(PATIENT_TABS.BASIC);

  // Merge over initialFormData so drafts saved before new fields were added
  // (e.g. couple fields) don't crash on missing nested objects.
  const [formData, setFormData] = useState(
    savedDraft ? { ...initialFormData, ...savedDraft } : initialFormData
  );
  const [errors, setErrors] = useState({});
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  // Staged files & folders for the "תיק מטופל" section (stored locally until save)
  const [stagedData, setStagedData] = useState({ files: [], folders: [] });
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const handleStagedDataChange = useCallback((data) => {
    setStagedData(data);
  }, []);

  const [createPatient, { isLoading }] = useCreatePatientMutation();

  // Track form changes with useMemo for better performance
  const isDirty = useMemo(() => {
    const formChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    const hasFiles = stagedData.files.length > 0 || stagedData.folders.length > 0;
    return formChanged || hasFiles;
  }, [formData, stagedData]);

  // Register unsaved changes with global context (handles sidebar navigation blocking)
  useRegisterUnsavedChanges(isDirty);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is edited
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const handlePartnerChange = (partnerKey, field, value) => {
    handleNestedChange(partnerKey, field, value);
    const errKey = `${partnerKey}${PARTNER_ERROR_SUFFIX[field]}`;
    if (errors[errKey]) {
      setErrors((prev) => ({ ...prev, [errKey]: null }));
    }
  };

  const handleCoupleToggle = (checked) => {
    setFormData((prev) => {
      if (checked) {
        // Prefill partner 1 from whatever was already typed in the single fields
        const hasPartnerData =
          prev.partner1?.firstName || prev.partner1?.phone;
        const partner1 = hasPartnerData
          ? prev.partner1
          : {
              ...emptyPartner(),
              firstName: prev.firstName,
              lastName: prev.lastName,
              phone: prev.phone,
            };
        return { ...prev, isCouple: true, partner1 };
      }
      // Turning off: keep partner data, restore single fields from the primary partner
      const primary =
        prev.smsPrimaryPartner === 2 ? prev.partner2 : prev.partner1;
      return {
        ...prev,
        isCouple: false,
        firstName: primary?.firstName || prev.firstName,
        lastName: primary?.lastName || prev.lastName,
        phone: primary?.phone || prev.phone,
      };
    });
    setErrors({});
  };

  const handleLanguageToggle = (lang) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  const formatPhone = (value) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, "");
    // Limit to 10 digits
    return digits.slice(0, 10);
  };

  const formatIdNumber = (value) => {
    // Remove non-digits and limit to 9 digits
    const digits = value.replace(/\D/g, "");
    return digits.slice(0, 9);
  };

  const validateBasicFields = () => {
    const newErrors = {};

    if (formData.isCouple) {
      ["partner1", "partner2"].forEach((key) => {
        const partner = formData[key] || {};
        if (!partner.firstName?.trim()) {
          newErrors[`${key}FirstName`] = "שדה חובה";
        }
        if (!partner.lastName?.trim()) {
          newErrors[`${key}LastName`] = "שדה חובה";
        }
        if (!partner.phone?.trim()) {
          newErrors[`${key}Phone`] = "שדה חובה";
        } else if (partner.phone.length !== 10) {
          newErrors[`${key}Phone`] = "מספר טלפון צריך להיות 10 ספרות";
        }
        if (partner.idNumber && partner.idNumber.length !== 9) {
          newErrors[`${key}Id`] = "תעודת זהות חייבת להכיל בדיוק 9 ספרות";
        }
      });
    } else {
      if (!formData.firstName.trim()) {
        newErrors.firstName = "שדה חובה";
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = "שדה חובה";
      }
      if (!formData.phone.trim()) {
        newErrors.phone = "שדה חובה";
      } else if (formData.phone.length !== 10) {
        newErrors.phone = "מספר טלפון צריך להיות 10 ספרות";
      }
    }

    if (formData.parent1?.id && formData.parent1.id.length !== 9) {
      newErrors.parent1Id = "תעודת זהות חייבת להכיל בדיוק 9 ספרות";
    }
    if (formData.parent2?.id && formData.parent2.id.length !== 9) {
      newErrors.parent2Id = "תעודת זהות חייבת להכיל בדיוק 9 ספרות";
    }

    if (therapistRequired && !selectedTherapistId) {
      newErrors.therapistId = "יש לבחור את המטפל/ת של המטופל/ת";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (asDraft = false) => {
    if (!asDraft && !validateBasicFields()) {
      setActiveTab(PATIENT_TABS.BASIC);
      return;
    }

    // Validate that we have required IDs from auth
    if (!clinicId || !effectiveTherapistId) {
      console.error("[CK-2002] Missing required auth data:", {
        errorCode: "CK-2002",
        errorMessage:
          "Missing clinicId or therapistId - user profile may not be loaded",
        clinicId,
        therapistId: effectiveTherapistId,
        timestamp: new Date().toISOString(),
      });
      setErrors({ submit: "שגיאה בטעינת פרטי המשתמש. נסה לרענן את הדף." });
      return;
    }

    // Check plan permission for creating patients
    const canProceed = await checkPatient();
    if (!canProceed) {
      // Modal is shown automatically by the hook
      return;
    }

    try {
      // Step 1: Create the patient
      const result = await createPatient({
        clinicId,
        therapistId: effectiveTherapistId,
        therapistName: effectiveTherapistName,
        enableAiPatientSummary: clinicProfile?.enableAiPatientSummary === true,
        patientData: {
          ...formData,
          isDraft: asDraft,
        },
      }).unwrap();

      const newPatientId = result.id;

      // Step 2: Upload staged files & folders (if any)
      if (stagedData.files.length > 0 || stagedData.folders.length > 0) {
        setIsUploadingFiles(true);
        try {
          // Build a mapping from staging folder IDs to real Firestore folder IDs
          const folderIdMap = {};

          // Create folders first
          for (const folder of stagedData.folders) {
            try {
              const createdFolder = await createPatientFolder(
                clinicId,
                newPatientId,
                { name: folder.name, color: folder.color },
                effectiveTherapistId,
                effectiveTherapistName
              );
              folderIdMap[folder.id] = createdFolder.id;
            } catch (folderError) {
              console.error("[CK-2003] Error creating folder during patient creation:", {
                errorCode: "CK-2003",
                errorMessage: folderError.message,
                folderName: folder.name,
                patientId: newPatientId,
                timestamp: new Date().toISOString(),
              });
              // Continue with other folders/files even if one fails
            }
          }

          // Upload files
          for (const stagedFile of stagedData.files) {
            try {
              // Map the staging folder ID to the real folder ID
              const realFolderId = stagedFile.folderId
                ? folderIdMap[stagedFile.folderId] || null
                : null;

              // Upload file to Firebase Storage
              const uploadResult = await uploadPatientFile(
                stagedFile.file,
                clinicId,
                newPatientId,
                effectiveTherapistId
              );

              // Create file record in Firestore
              await createPatientFile(
                clinicId,
                newPatientId,
                {
                  fileName: stagedFile.file.name,
                  fileSize: stagedFile.file.size,
                  fileType: stagedFile.file.type,
                  fileUrl: uploadResult.url,
                  storagePath: uploadResult.storagePath,
                  folderId: realFolderId,
                  description: stagedFile.description || null,
                },
                effectiveTherapistId,
                effectiveTherapistName
              );
            } catch (fileError) {
              console.error("[CK-2004] Error uploading file during patient creation:", {
                errorCode: "CK-2004",
                errorMessage: fileError.message,
                fileName: stagedFile.fileName,
                patientId: newPatientId,
                timestamp: new Date().toISOString(),
              });
              // Continue with other files even if one fails
            }
          }
        } finally {
          setIsUploadingFiles(false);
        }
      }

      // Persist any custom referral source to the therapist's saved list for next time
      await persistCustomReferralSource({
        value: formData.referralSource,
        therapistId: effectiveTherapistId,
        therapistProfile,
        dispatch,
      });

      dispatch(clearFormDraft());
      showSuccess(asDraft ? "הטיוטא נשמרה בהצלחה" : "המטופל נוצר בהצלחה");
      navigate(`/dashboard/patients/${result.id}`);
    } catch (error) {
      console.error("[CK-2001] Error creating patient:", {
        errorCode: "CK-2001",
        errorMessage: error.message,
        clinicId,
        therapistId: effectiveTherapistId,
        isDraft: asDraft,
        timestamp: new Date().toISOString(),
      });
      const isAuthError =
        error.message?.includes("auth/") ||
        error.code === "auth/user-token-expired";
      showError(
        isAuthError
          ? "פג תוקף החיבור. אנא התחבר מחדש."
          : "שגיאה ביצירת המטופל"
      );
    }
  };

  const handleSafeClose = () => {
    if (isDirty) {
      setShowCloseWarning(true);
    } else {
      navigate("/dashboard/patients");
    }
  };

  const handleDiscardChanges = () => {
    dispatch(clearFormDraft());
    navigate("/dashboard/patients");
  };

  const saveDisabled =
    isLoading || isUploadingFiles || !clinicId || !effectiveTherapistId;

  return {
    // tabs
    activeTab,
    setActiveTab,
    // form state
    formData,
    errors,
    handleChange,
    handleNestedChange,
    handlePartnerChange,
    handleCoupleToggle,
    handleLanguageToggle,
    formatPhone,
    formatIdNumber,
    // context
    clinicManagesCouples,
    englishCommunicationEnabled,
    isSpeechTherapist,
    canAccessFinance,
    canAssignToOtherTherapist,
    isSecretary,
    therapistRequired,
    creationBlocked,
    activeTherapists,
    selectedTherapistId,
    setSelectedTherapistId,
    therapistId,
    effectiveTherapistName,
    therapistProfile,
    patientTypes,
    treatmentTypes,
    // files staging
    stagedData,
    handleStagedDataChange,
    // submit / close
    handleSubmit,
    handleSafeClose,
    handleDiscardChanges,
    showCloseWarning,
    setShowCloseWarning,
    isLoading,
    isUploadingFiles,
    isChecking,
    saveDisabled,
    PlanLimitModalComponent,
  };
}

export default usePatientForm;
